import { useEffect, useState, useCallback } from "react";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DateField } from "./DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, CalendarClock } from "lucide-react";

const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long" }) : "—");
// "21–27 settembre" when same month, otherwise both extended dates.
const fmtRange = (a) => {
  if (!a?.date_to) return fmt(a?.date);
  const f = a.date_from || a.date;
  const df = new Date(f + "T00:00:00"), dt = new Date(a.date_to + "T00:00:00");
  if (df.getMonth() === dt.getMonth() && df.getFullYear() === dt.getFullYear())
    return `${df.getDate()}–${dt.getDate()} ${dt.toLocaleDateString("it-IT", { month: "long" })}`;
  return `${fmt(f)} – ${fmt(a.date_to)}`;
};

const isoOf = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const weekRange = (offsetWeeks) => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const dow = (now.getDay() + 6) % 7; // Monday = 0
  const mon = new Date(now); mon.setDate(now.getDate() - dow + offsetWeeks * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: isoOf(mon), to: isoOf(sun) };
};
const nextMonthRange = () => {
  const now = new Date();
  return { from: isoOf(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
           to: isoOf(new Date(now.getFullYear(), now.getMonth() + 2, 0)) };
};

export default function FarrierAppointments({ stableId, canOps, compact = false, reference = false }) {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // {id?, mode, farrier, date, date_from, date_to, time, note}
  const [names, setNames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  const load = useCallback(() => {
    if (!stableId) return;
    api.get(`/stables/${stableId}/farrier/appointments`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [stableId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (stableId) api.get(`/stables/${stableId}/farrier/names`).then((r) => setNames(r.data)).catch(() => {}); }, [stableId]);

  const openNew = () => setDialog({ mode: "exact", farrier: "", date: "", date_from: "", date_to: "", time: "", note: "" });
  const openEdit = (a) => setDialog(a.date_to
    ? { ...a, mode: "period", date_from: a.date_from || a.date, date_to: a.date_to }
    : { ...a, mode: "exact", date_from: "", date_to: "" });

  const save = async () => {
    if (!dialog.farrier?.trim()) { toast.error("Il maniscalco è obbligatorio"); return; }
    let body;
    if (dialog.mode === "period") {
      if (!dialog.date_from || !dialog.date_to) { toast.error("Indica il periodo (Da / A)"); return; }
      if (dialog.date_to < dialog.date_from) { toast.error("La data 'A' deve essere successiva a 'Da'"); return; }
      body = { farrier: dialog.farrier.trim(), date: dialog.date_from, date_from: dialog.date_from,
               date_to: dialog.date_to, time: dialog.time || null, note: dialog.note || null };
    } else {
      if (!dialog.date) { toast.error("Indica la data"); return; }
      body = { farrier: dialog.farrier.trim(), date: dialog.date, date_from: null,
               date_to: null, time: dialog.time || null, note: dialog.note || null };
    }
    setBusy(true);
    try {
      if (dialog.id) await api.put(`/stables/${stableId}/farrier/appointments/${dialog.id}`, body);
      else await api.post(`/stables/${stableId}/farrier/appointments`, body);
      toast.success("Passaggio salvato"); setDialog(null); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/stables/${stableId}/farrier/appointments/${del.id}`); toast.success("Passaggio eliminato"); setDel(null); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const applyQuick = (range) => setDialog((d) => ({ ...d, mode: "period", date_from: range.from, date_to: range.to }));

  const dialogEl = (
    <>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="farrier-appt-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">{dialog?.id ? "Modifica passaggio" : "Nuovo passaggio maniscalco"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div><Label>Maniscalco *</Label>
                <Input list="farrier-names" className="mt-1.5" data-testid="appt-farrier" value={dialog.farrier} onChange={(e) => setDialog({ ...dialog, farrier: e.target.value })} placeholder="Nome maniscalco" />
                <datalist id="farrier-names">{names.map((n) => <option key={n} value={n} />)}</datalist></div>

              <div>
                <Label>Quando</Label>
                <div className="mt-1.5 inline-flex rounded-lg border border-[#E5DEC9] bg-white overflow-hidden">
                  <button type="button" data-testid="appt-mode-exact" onClick={() => setDialog({ ...dialog, mode: "exact" })}
                    className={`px-3 py-1.5 text-sm ${dialog.mode !== "period" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>Data esatta</button>
                  <button type="button" data-testid="appt-mode-period" onClick={() => setDialog({ ...dialog, mode: "period" })}
                    className={`px-3 py-1.5 text-sm ${dialog.mode === "period" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>Periodo previsto</button>
                </div>
              </div>

              {dialog.mode === "period" ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Da *</Label><DateField className="mt-1.5" testId="appt-date-from" value={dialog.date_from} onChange={(v) => setDialog({ ...dialog, date_from: v })} allowFuture /></div>
                    <div><Label>A *</Label><DateField className="mt-1.5" testId="appt-date-to" value={dialog.date_to} onChange={(v) => setDialog({ ...dialog, date_to: v })} allowFuture /></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" data-testid="appt-quick-nextweek" onClick={() => applyQuick(weekRange(1))} className="text-xs rounded-full border border-[#E5DEC9] px-2.5 py-1 text-[#8C532B] hover:bg-[#F0EAE1]">Prossima settimana</button>
                    <button type="button" data-testid="appt-quick-2w" onClick={() => applyQuick(weekRange(2))} className="text-xs rounded-full border border-[#E5DEC9] px-2.5 py-1 text-[#8C532B] hover:bg-[#F0EAE1]">Tra 2 settimane</button>
                    <button type="button" data-testid="appt-quick-3w" onClick={() => applyQuick(weekRange(3))} className="text-xs rounded-full border border-[#E5DEC9] px-2.5 py-1 text-[#8C532B] hover:bg-[#F0EAE1]">Tra 3 settimane</button>
                    <button type="button" data-testid="appt-quick-nextmonth" onClick={() => applyQuick(nextMonthRange())} className="text-xs rounded-full border border-[#E5DEC9] px-2.5 py-1 text-[#8C532B] hover:bg-[#F0EAE1]">Mese prossimo</button>
                  </div>
                  <p className="text-xs text-[#8C6D4F]">Promemoria di massima: modificabile e convertibile in data esatta quando il maniscalco conferma il giorno.</p>
                </>
              ) : (
                <div><Label>Data *</Label>
                  <DateField className="mt-1.5" testId="appt-date" value={dialog.date} onChange={(v) => setDialog({ ...dialog, date: v })} allowFuture /></div>
              )}

              <div><Label>Ora (opzionale)</Label>
                <Input type="time" className="mt-1.5" data-testid="appt-time" value={dialog.time || ""} onChange={(e) => setDialog({ ...dialog, time: e.target.value })} /></div>
              <div><Label>Nota (opzionale)</Label>
                <Textarea className="mt-1.5" data-testid="appt-note" value={dialog.note || ""} onChange={(e) => setDialog({ ...dialog, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button>
            <Button onClick={save} disabled={busy} data-testid="appt-save" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent data-testid="farrier-appt-del" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display text-red-700">Eliminare il passaggio?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5F6B64]">{del?.farrier} · {fmtRange(del || {})}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>Annulla</Button>
            <Button onClick={remove} data-testid="appt-del-confirm" className="bg-red-600 hover:bg-red-700 text-white">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const next = items[0];
  const label = (a) => `${fmtRange(a)}${a.time ? ` · ${a.time}` : ""} · ${a.farrier}`;
  const rowFull = (a) => (
    <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`appt-row-${a.id}`}>
      <div className="min-w-0 flex items-center gap-2.5">
        <CalendarClock className="h-4 w-4 text-[#8C532B] shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#1B3B2B] truncate">{label(a)}{a.date_to ? <span className="ml-1.5 text-[10px] font-mono-label uppercase text-[#8C6D4F]">periodo</span> : null}</p>
          {a.note && <p className="text-xs text-[#5F6B64] truncate">{a.note}</p>}
        </div>
      </div>
      {canOps && (
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" data-testid={`appt-edit-${a.id}`} onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" data-testid={`appt-del-${a.id}`} onClick={() => setDel(a)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      )}
    </div>
  );

  if (compact) {
    if (reference) {
      return (
        <div data-testid="farrier-appt-reference-wrap">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm px-1" data-testid="farrier-appt-reference">
            <span className="font-mono-label text-[10px] uppercase text-[#8C532B] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Prossimo passaggio maniscalco</span>
            {next
              ? <span className="font-medium text-[#1B3B2B]">{label(next)}</span>
              : <span className="text-[#5F6B64]">non impostato</span>}
            {items.length > 1 && <span className="text-xs text-[#8C6D4F]">+{items.length - 1} altri</span>}
            {canOps && (
              <button type="button" data-testid="farrier-appt-manage" onClick={() => setManageOpen(true)}
                className="text-xs font-semibold text-[#8C532B] hover:text-[#5A3419] underline underline-offset-2">Gestisci</button>
            )}
          </div>
          <Dialog open={manageOpen} onOpenChange={setManageOpen}>
            <DialogContent data-testid="farrier-appt-manage-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#8C532B]" /> Passaggi maniscalco</DialogTitle></DialogHeader>
              {canOps && <div className="flex justify-end"><Button size="sm" data-testid="appt-new" onClick={openNew} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button></div>}
              {items.length === 0
                ? <p className="text-sm text-[#5F6B64] py-2" data-testid="appt-empty">Nessun passaggio in programma.</p>
                : <div className="rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden bg-white">{items.map(rowFull)}</div>}
            </DialogContent>
          </Dialog>
          {dialogEl}
        </div>
      );
    }
    // Dashboard: integrated Mascalcia module — includes the discreet temporal bar (Dashboard ONLY).
    return (
      <section data-testid="dash-next-farrier-appointment">
        <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
          <h3 className="font-display font-semibold text-[#1B3B2B] text-sm">Prossimo passaggio</h3>
          {canOps && items.length > 0 && (
            <button type="button" data-testid="dash-appt-add" onClick={openNew}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#F0EAE1] text-[#8C532B] hover:brightness-95 transition"><Plus className="h-3 w-3" /> Aggiungi</button>
          )}
        </div>
        <div className="rounded-xl border border-[#E5DEC9] overflow-hidden bg-white">
          <div className="divide-y divide-[#F0EAE1]">
            {items.length === 0 ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid="appt-empty">
                <span className="text-sm text-[#5F6B64]">Non impostato</span>
                {canOps && <Button size="sm" variant="outline" data-testid="dash-appt-add" onClick={openNew}><Plus className="h-3 w-3 mr-1" />Aggiungi</Button>}
              </div>
            ) : items.map(rowFull)}
          </div>
        </div>
        {dialogEl}
      </section>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5DEC9] p-4" data-testid="farrier-appointments">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-semibold text-[#1B3B2B] flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#8C532B]" /> Prossimi passaggi maniscalco</h3>
        {canOps && <Button size="sm" data-testid="appt-new" onClick={openNew} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#5F6B64] py-2" data-testid="appt-empty">Prossimo passaggio non impostato.</p>
      ) : (
        <div className="divide-y divide-[#F0EAE1]">{items.map(rowFull)}</div>
      )}
      {dialogEl}
    </div>
  );
}
